import { Package, MapPin, Truck, Users, ArrowRight, ShieldCheck, Bluetooth, Recycle, Lightning, MapTrifold, ChartBar, SignOut, User, Translate } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EnvironmentalCalculator from "@/components/EnvironmentalCalculator";
import RoleWelcomeCard from "@/components/RoleWelcomeCard";
import { getNavItems, getRoleConfig } from "@/lib/roleNav";

const LANGS = [
  { code: "pt", label: "PT", flag: "🇵🇹" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "es", label: "ES", flag: "🇪🇸" },
];

function LandingLangSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGS.find(l => i18n.language?.startsWith(l.code)) || LANGS[0];
  return (
    <div style={{ position: "relative" }} data-testid="landing-lang-switcher">
      <button
        data-testid="landing-lang-toggle"
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: "var(--text-main)" }}
      >
        <Translate size={14} weight="duotone" />
        <span>{current.flag} {current.label}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--surface)", border: "1px solid var(--border-color)", borderRadius: 12, boxShadow: "0 12px 30px -10px rgba(0,0,0,0.18)", overflow: "hidden", zIndex: 100, minWidth: 130 }}>
          {LANGS.map(l => (
            <button
              key={l.code}
              data-testid={`landing-lang-${l.code}`}
              onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", cursor: "pointer", fontFamily: "Manrope", fontSize: "0.8rem", background: i18n.language?.startsWith(l.code) ? "rgba(45,74,62,0.08)" : "transparent", color: "var(--text-main)", fontWeight: i18n.language?.startsWith(l.code) ? 700 : 500, textAlign: "left" }}
            >
              <span>{l.flag}</span> <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const IMAGES = {
  hero: "/assets/bags.png",
  howItWorks: "/assets/hero.png",
};

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="bento-item p-6 flex flex-col gap-2">
    <Icon size={28} weight="duotone" style={{ color: "var(--primary)" }} />
    <span style={{ fontFamily: "Outfit", fontSize: "2rem", fontWeight: 600, color: "var(--text-main)" }}>{value}</span>
    <span className="overline">{label}</span>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc, delay }) => (
  <div className={`bento-item p-8 card-hover animate-fade-in-up stagger-${delay}`}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(45,74,62,0.08)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
      <Icon size={24} weight="duotone" style={{ color: "var(--primary)" }} />
    </div>
    <h3 style={{ fontFamily: "Outfit", fontSize: "1.25rem", fontWeight: 600, marginBottom: 8 }}>{title}</h3>
    <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", lineHeight: 1.6 }}>{desc}</p>
  </div>
);

const StepCard = ({ number, title, desc }) => (
  <div className="flex gap-5 animate-fade-in-up" style={{ animationDelay: `${number * 0.15}s`, opacity: 0 }}>
    <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--primary)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", flexShrink: 0 }}>
      {number}
    </div>
    <div>
      <h4 style={{ fontFamily: "Outfit", fontSize: "1.1rem", fontWeight: 600, marginBottom: 4 }}>{title}</h4>
      <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", lineHeight: 1.6 }}>{desc}</p>
    </div>
  </div>
);

// Bag format showcase
const BAG_FORMATS = [
  { name: "Small", desc: "Documentos e pequenos objetos", weight: "ate 1kg", size: "25x20cm", material: "Tecido ultraleve" },
  { name: "Medium", desc: "Uso geral, o mais versátil", weight: "ate 3kg", size: "35x30cm", material: "Tecido ultraleve" },
  { name: "Large", desc: "Objetos maiores", weight: "ate 5kg", size: "50x40cm", material: "Tecido ultraleve" },
  { name: "Fragile", desc: "Objetos delicados", weight: "ate 3kg", size: "35x30cm", material: "Tecido almofadado" },
];

export default function LandingPage({ stats, setCurrentView, loading, user, demoMode, onLogout }) {
  const { t } = useTranslation();
  const roleCfg = user ? getRoleConfig(user.role) : null;
  // Logged-in users get their role-specific menu; everyone else gets the
  // public marketing nav. Both are produced by the shared helper.
  const headerNavItems = user
    ? getNavItems(user.role).filter((it) => it.id !== "landing")
    : null; // null = render the original marketing nav (admin gets full menu via getNavItems)
  return (
    <div data-testid="landing-page">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Bluemove" style={{ height: 36 }} />
          </div>
          <nav className="hidden md:flex items-center gap-2">
            {headerNavItems
              ? headerNavItems.map((it) => {
                  const role = user?.role;
                  const isAdminItem = it.id === "admin";
                  const label = isAdminItem && role === "super_admin"
                    ? "Super Admin"
                    : t(it.labelKey);
                  return (
                    <button
                      key={it.id}
                      data-testid={`nav-${it.id}-demo`}
                      className="nav-item"
                      onClick={() => setCurrentView(it.id)}
                      style={isAdminItem ? { color: role === "super_admin" ? "#dc2626" : "#7c3aed", fontWeight: role === "super_admin" ? 700 : 600 } : undefined}
                    >
                      {label}
                    </button>
                  );
                })
              : (<>
                  <button data-testid="nav-user-demo" className="nav-item" onClick={() => setCurrentView("user")}>Envios</button>
                  <button data-testid="nav-tracking-demo" className="nav-item" onClick={() => setCurrentView("tracking")}>Rastrear</button>
                  <button data-testid="nav-intermediary-demo" className="nav-item" onClick={() => setCurrentView("intermediary")}>Intermediarios</button>
                  <button data-testid="nav-driver-demo" className="nav-item" onClick={() => setCurrentView("driver")}>Motoristas</button>
                  <button data-testid="nav-map-demo" className="nav-item" onClick={() => setCurrentView("map")}>Mapa</button>
                  <button data-testid="nav-analytics-demo" className="nav-item" onClick={() => setCurrentView("analytics")}>Analytics</button>
                  <button data-testid="nav-beacons-demo" className="nav-item" onClick={() => setCurrentView("beacons")}>Beacons</button>
                </>)
            }
          </nav>
          <div className="flex items-center gap-2">
            {/* Language switcher — small inline version, both for logged-in and demo users */}
            <LandingLangSwitcher />
            {user && (
              <>
                <div className="hidden sm:flex items-center gap-2" style={{ padding: "4px 12px", borderRadius: 9999, background: "rgba(45,74,62,0.06)", fontSize: "0.8rem" }}>
                  <User size={14} weight="duotone" style={{ color: "var(--primary)" }} />
                  <span style={{ fontWeight: 600 }}>{user.name}</span>
                </div>
                <button data-testid="landing-logout-btn" onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", padding: 6 }}>
                  <SignOut size={18} weight="bold" />
                </button>
              </>
            )}
            {!user && !demoMode && (
              <button data-testid="get-started-btn" onClick={() => setCurrentView("user")} style={{ background: "var(--primary)", color: "white", padding: "10px 24px", borderRadius: 9999, border: "none", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer" }}>
                Comecar
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Role-specific welcome dashboard (hidden for admins/anon) */}
      <RoleWelcomeCard user={user} setCurrentView={setCurrentView} />

      {/* Hero */}
      <section className="pt-28 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <span className="overline" style={{ color: "var(--accent)", marginBottom: 16, display: "block" }}>Sistema de entregas inteligente</span>
            <h1 style={{ fontFamily: "Outfit", fontSize: "clamp(2.2rem, 5vw, 3.5rem)", fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.03em", marginBottom: 20, color: "var(--text-main)" }}>
              Bolsas inteligentes para entregas
              <span style={{ color: "var(--primary)" }}> descentralizadas</span>
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "1.1rem", lineHeight: 1.7, maxWidth: 540, marginBottom: 32 }}>
              BluMove revoluciona o envio de encomendas com bolsas reutilizaveis equipadas com tecnologia Bluetooth, numa rede descentralizada de pontos de recolha e entrega.
            </p>
            <div className="flex flex-wrap gap-3">
              {(() => {
                const cta = roleCfg?.hero || { view: "user", labelKey: "role_cta.user" };
                const ctaColor = roleCfg?.color || "var(--primary)";
                return (
                  <button data-testid="hero-cta-primary" onClick={() => setCurrentView(cta.view)}
                    style={{ background: ctaColor, color: "white", padding: "14px 32px", borderRadius: 9999, border: "none", fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "filter 0.25s, transform 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}
                  >
                    {t(cta.labelKey)} <ArrowRight size={18} weight="bold" />
                  </button>
                );
              })()}
              <button data-testid="hero-cta-map" onClick={() => setCurrentView("map")}
                style={{ background: "transparent", color: "var(--primary)", padding: "14px 32px", borderRadius: 9999, border: "2px solid var(--border-color)", fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.25s" }}
                onMouseEnter={e => { e.target.style.borderColor = "var(--primary)"; e.target.style.background = "rgba(45,74,62,0.04)"; }}
                onMouseLeave={e => { e.target.style.borderColor = "var(--border-color)"; e.target.style.background = "transparent"; }}
              >
                <MapTrifold size={18} weight="duotone" /> Ver mapa da rede
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-in-up stagger-2">
            <div style={{ position: "relative" }}>
              <img src={IMAGES.hero} alt="Bolsas BluMove no compartimento de bagagens de autocarro" style={{ width: "100%", height: 400, objectFit: "cover", borderRadius: 20, border: "1px solid var(--border-color)" }} />
              <div className="animate-float" style={{ position: "absolute", bottom: -16, left: -16, background: "var(--surface)", border: "1px solid var(--border-color)", borderRadius: 14, padding: "12px 18px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e", animation: "pulse-dot 2s infinite" }} />
                <span style={{ fontFamily: "JetBrains Mono", fontWeight: 500, fontSize: "0.85rem", color: "var(--primary)" }}>ESO4015</span>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Em transito</span>
              </div>
              <div className="animate-float" style={{ position: "absolute", top: -12, right: -12, background: "var(--primary)", borderRadius: 12, padding: "8px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 6, animationDelay: "1.5s" }}>
                <Package size={16} weight="fill" color="white" />
                <span style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.75rem", color: "white" }}>4 bolsas ativas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="px-6 pb-16" data-testid="stats-section">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Package} value={stats.total_bags} label="Bolsas no circuito" />
            <StatCard icon={MapPin} value={stats.total_points} label="Pontos de recolha" />
            <StatCard icon={Truck} value={stats.in_transit} label="Em transito" />
            <StatCard icon={Users} value={stats.active_drivers} label="Motoristas ativos" />
          </div>
        </section>
      )}

      {/* Features */}
      <section className="px-6 pb-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 8 }}>Caracteristicas</span>
            <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em" }}>Tecnologia que revoluciona entregas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard icon={Bluetooth} title="Fecho Bluetooth" desc="Sistema de trinco acionado por Bluetooth para seguranca maxima. Apenas utilizadores autorizados podem abrir a bolsa." delay={1} />
            <FeatureCard icon={Recycle} title="Bolsas Reutilizaveis" desc="4 formatos de bolsas em tecido ultraleve ou almofadado, de facil limpeza. Sempre em circulacao no sistema." delay={2} />
            <FeatureCard icon={ShieldCheck} title="Beacon Integrado" desc="Dispositivo Bluetooth Beacon camuflado na bolsa para rastreamento em tempo real via GPS." delay={3} />
            <FeatureCard icon={Lightning} title="Rede Descentralizada" desc="Estacoes, quiosques, papelarias e estafetas. Sempre perto de si." delay={4} />
          </div>
        </div>
      </section>

      {/* Bag formats */}
      <section className="px-6 pb-20" data-testid="bag-formats-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 8 }}>Formatos de bolsa</span>
            <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em" }}>Bolsa certa para cada envio</h2>
            <p style={{ color: "var(--text-muted)", maxWidth: 500, margin: "8px auto 0", lineHeight: 1.6 }}>Cada bolsa possui ID visivel (ex: BLU4015), janela para dados, pegas ergonomicas e fecho Bluetooth.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BAG_FORMATS.map((bag, i) => (
              <div key={i} className={`bento-item p-6 card-hover animate-fade-in-up stagger-${i+1}`}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                  <Package size={28} weight="duotone" style={{ color: "var(--primary)" }} />
                </div>
                <h4 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", marginBottom: 4 }}>{bag.name}</h4>
                <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginBottom: 12 }}>{bag.desc}</p>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between" style={{ fontSize: "0.75rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Peso</span>
                    <span style={{ fontWeight: 600 }}>{bag.weight}</span>
                  </div>
                  <div className="flex justify-between" style={{ fontSize: "0.75rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Tamanho</span>
                    <span style={{ fontWeight: 600 }}>{bag.size}</span>
                  </div>
                  <div className="flex justify-between" style={{ fontSize: "0.75rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Material</span>
                    <span style={{ fontWeight: 600 }}>{bag.material}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Calculator */}
      <EnvironmentalCalculator />

      {/* How it works */}
      <section className="px-6 pb-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 8 }}>Como funciona</span>
            <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 8 }}>Simples e eficiente</h2>
            <p style={{ color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 32 }}>Envie encomendas de forma sustentavel usando a rede BluMove.</p>
            <div className="flex flex-col gap-8">
              <StepCard number={1} title="Crie o envio" desc="Registe a encomenda na app e escolha os pontos de recolha e entrega." />
              <StepCard number={2} title="Deposite no ponto" desc="Leve ao ponto de recolha. Uma bolsa inteligente e atribuida automaticamente." />
              <StepCard number={3} title="Transporte descentralizado" desc="A bolsa viaja por autocarros, comboios ou estafetas." />
              <StepCard number={4} title="Recolha pelo destinatario" desc="Recolha no ponto de entrega. A bolsa volta ao circuito automaticamente." />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="bento-item overflow-hidden" style={{ borderRadius: 20 }}>
              <img src={IMAGES.howItWorks} alt="Como usar a bolsa BluMove - guia passo a passo" style={{ width: "100%", height: "auto", objectFit: "contain", display: "block" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Network Points */}
      <section className="px-6 pb-20" data-testid="network-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 8 }}>Rede de pontos</span>
            <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em" }}>Pontos de recolha e entrega</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Truck, label: "Estacoes de Autocarro", desc: "Integrado com transportes publicos" },
              { icon: MapPin, label: "Estacoes de Comboio", desc: "Nos principais terminais ferroviarios" },
              { icon: Users, label: "Estafetas", desc: "Rede de estafetas dedicados" },
              { icon: Package, label: "Papelarias e Quiosques", desc: "Pontos de conveniencia locais" },
            ].map((item, i) => (
              <div key={i} className={`bento-item p-8 text-center card-hover animate-fade-in-up stagger-${i+1}`}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <item.icon size={28} weight="duotone" style={{ color: "var(--primary)" }} />
                </div>
                <h4 style={{ fontFamily: "Outfit", fontWeight: 600, marginBottom: 4 }}>{item.label}</h4>
                <p style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto" style={{ background: "var(--primary)", borderRadius: 24, padding: "48px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 700, color: "white", marginBottom: 12 }}>Explore a plataforma BluMove</h2>
          <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: 28, maxWidth: 480, margin: "0 auto 28px" }}>Descubra todas as funcionalidades: envios, mapa interativo, analytics e gestao de rede.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button data-testid="cta-map" onClick={() => setCurrentView("map")} style={{ background: "white", color: "var(--primary)", padding: "12px 28px", borderRadius: 9999, border: "none", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: 6 }}>
              <MapTrifold size={18} /> Mapa Interativo
            </button>
            <button data-testid="cta-analytics" onClick={() => setCurrentView("analytics")} style={{ background: "rgba(255,255,255,0.15)", color: "white", padding: "12px 28px", borderRadius: 9999, border: "1px solid rgba(255,255,255,0.3)", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: 6 }}>
              <ChartBar size={18} /> Analytics
            </button>
            <button data-testid="cta-user-demo" onClick={() => setCurrentView("user")} style={{ background: "rgba(255,255,255,0.15)", color: "white", padding: "12px 28px", borderRadius: 9999, border: "1px solid rgba(255,255,255,0.3)", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}>
              Criar Envio
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 pb-12 pt-8" style={{ borderTop: "1px solid var(--border-color)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="Bluemove" style={{ height: 20 }} />
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>Bolsas inteligentes para entregas descentralizadas - Sistema inovador de logistica sustentavel</p>
          <div className="flex items-center gap-4">
            <button data-testid="footer-terms" onClick={() => setCurrentView("terms")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.8rem", fontWeight: 600 }}>Termos</button>
            <button data-testid="footer-privacy" onClick={() => setCurrentView("privacy")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.8rem", fontWeight: 600 }}>Privacidade</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
